/**
 * The Ads AI page (pages/AdsAi.tsx): types, the starter questions, how a proposal's
 * state reads, and the NDJSON stream reader. Only erasable types, so node --test
 * loads this file directly (test/ads-ai-ui.test.js).
 */
export type AiProposal = {
  id: number; title: string; action_type: string; risk_level: string; status: string;
  assessment?: Record<string, string> | null; is_ai_generated?: boolean; submitted_by?: number; ads_status?: string | null; ads_error?: string | null;
};
export type AiMessage = {
  id: number | string; role: "user" | "assistant"; content: string; created_at?: string;
  steps?: string[]; proposals?: AiProposal[]; provider?: string | null; model?: string | null; failed?: boolean;
};
export type AiStreamEvent =
  | { type: "ping" }
  | { type: "session"; id: number; title?: string }
  | { type: "step"; label: string }
  | { type: "proposal"; proposal: AiProposal }
  | { type: "provider"; provider: string; model: string }
  | { type: "done"; message: AiMessage }
  | { type: "error"; error: string };

export const STARTERS = [
  "Weekly media buying review across Google Ads, LSA and Facebook: where should the next dollar go?",
  "Run the Google Ads audit SOP on this account, step by step",
  "Review this account; explain what to change and what to leave alone",
  "Where is money being wasted in search terms this month?",
  "Why did cost per conversion change over the last 2 weeks?",
  "Plan a new Search campaign for our main service",
];

/** Where a proposed change stands, in words, with a key for its look. */
export function proposalState(p: Pick<AiProposal, "status" | "ads_status"> & { action_type?: string }): { key: "waiting" | "applied" | "failed" | "skipped" | "undone"; label: string } {
  // Facebook changes go through Windsor: approved in Approvals, then run from the Facebook Ads page.
  const facebook = String(p.action_type || "").startsWith("windsor_");
  if (p.status === "pending") return { key: "waiting", label: facebook ? "Proposed Facebook change" : "Proposed change" };
  if (p.status === "executed") return { key: "applied", label: facebook ? "Applied in Facebook" : "Applied in Google Ads" };
  if (facebook && p.status === "approved") return { key: "waiting", label: "Approved, run it on the Facebook Ads page" };
  if (p.status === "rejected") return { key: "skipped", label: "Skipped" };
  if (p.status === "rolled_back") return { key: "undone", label: "Undone" };
  return { key: "failed", label: "Approved, not applied" };
}

/** Lines of JSON in, one event at a time out; a bad line is skipped, a non-200 throws its error. */
export function splitLines(buffer: string): { events: AiStreamEvent[]; rest: string } {
  const parts = buffer.split("\n");
  const rest = parts.pop() ?? "";
  const events: AiStreamEvent[] = [];
  for (const line of parts) {
    if (!line.trim()) continue;
    try { events.push(JSON.parse(line) as AiStreamEvent); } catch { /* a partial or foreign line */ }
  }
  return { events, rest };
}

export async function readStream<E extends { type: string } = AiStreamEvent>(path: string, body: unknown, onEvent: (ev: E) => void, signal?: AbortSignal, who = "The Ads AI"): Promise<void> {
  const res = await fetch(path, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/x-ndjson", "X-NL-AOS-Request": "1" },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok || !res.body) {
    const data = await res.json().catch(() => null) as { error?: string } | null;
    throw new Error(data?.error || `${who} could not start (${res.status}).`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "", completed = false;
  try {
    for (;;) {
      signal?.throwIfAborted();
      const { done, value } = await reader.read();
      signal?.throwIfAborted();
      if (value) buffer += decoder.decode(value, { stream: true });
      const { events, rest } = splitLines(done ? `${buffer}\n` : buffer);
      buffer = rest;
      for (const ev of events) { onEvent(ev as unknown as E); if (ev.type === "done") completed = true; }
      if (done) break;
    }
    if (!completed) throw new Error("The connection ended before the answer finished. Check the saved conversation before trying again.");
  } finally {
    await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}
